import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import OnboardingChecklist, { getOnboardingProgress } from "./OnboardingChecklist";
import {
  formatSubscriptionExpiry,
  formatStorageDashboardLabel,
  formatTeamUsageLabel,
  formatBusinessPlanLabel,
  getStorageUsagePercent,
  getSubscriptionAlert,
  getTrialStatusText,
  isTrialSubscription,
} from "../utils/userProfile";

function DashboardStorageLine({ storage }) {
  const storageLabel = formatStorageDashboardLabel(storage);
  const storagePercent = getStorageUsagePercent(storage);

  return (
    <span className="app-dashboard-sub__storage">
      <span className="app-dashboard-sub__storage-text">{storageLabel}</span>
      <span
        className="app-dashboard-sub__storage-meter"
        role="progressbar"
        aria-valuenow={storagePercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={storageLabel}
      >
        <span
          className={`app-dashboard-sub__storage-meter-fill${
            storagePercent >= 90 ? " app-dashboard-sub__storage-meter-fill--warn" : ""
          }`}
          style={{ width: `${storagePercent}%` }}
        />
      </span>
    </span>
  );
}

function DashboardSubscriptionChip({ businessSubscription, storage, teamUsage }) {
  const activeUntil = formatSubscriptionExpiry(businessSubscription);
  const isActive = Boolean(activeUntil);
  const isTrial = isTrialSubscription(businessSubscription);
  const alert = getSubscriptionAlert(businessSubscription, storage);
  const isWarning = isActive && alert?.severity === "warning";
  const planLabel = formatBusinessPlanLabel(businessSubscription);
  const teamLabel = formatTeamUsageLabel(teamUsage);
  const trialStatus = getTrialStatusText(businessSubscription);

  if (!isActive) {
    return (
      <Link to="/main/price" className="app-dashboard-sub app-dashboard-sub--inactive">
        <span className="app-dashboard-sub__dot" aria-hidden />
        <span className="app-dashboard-sub__body">
          <span className="app-dashboard-sub__label">Подписка</span>
          <strong className="app-dashboard-sub__status">Не активна</strong>
          <span className="app-dashboard-sub__hint">Нажмите, чтобы оформить</span>
          <DashboardStorageLine storage={storage} />
        </span>
      </Link>
    );
  }

  return (
    <Link
      to="/main/price"
      className={`app-dashboard-sub app-dashboard-sub--active${
        isTrial ? " app-dashboard-sub--trial" : ""
      }${isWarning ? " app-dashboard-sub--warning" : ""}`}
    >
      <span
        className={`app-dashboard-sub__dot app-dashboard-sub__dot--${
          isWarning ? "warning" : isTrial ? "trial" : "active"
        }`}
        aria-hidden
      />
      <span className="app-dashboard-sub__body">
        <span className="app-dashboard-sub__label">{planLabel}</span>
        <strong className="app-dashboard-sub__status">
          {isTrial
            ? isWarning
              ? "Триал скоро закончится"
              : "Триал активен"
            : isWarning
              ? "Скоро закончится"
              : "Активна"}
        </strong>
        <span className="app-dashboard-sub__hint">
          {trialStatus ??
            (isWarning && alert?.daysLeft
              ? `Осталось ${alert.daysLeft} дн. · до ${activeUntil}`
              : `Действует до ${activeUntil}`)}
          {teamLabel ? ` · ${teamLabel}` : ""}
        </span>
        <DashboardStorageLine storage={storage} />
      </span>
    </Link>
  );
}

function DashboardNextStepsHelper({ businessSubscription, games }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const { doneCount, total, allDone } = getOnboardingProgress(
    businessSubscription,
    games
  );

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  if (allDone) return null;

  return (
    <div className="app-dashboard-helper" ref={rootRef}>
      <button
        type="button"
        className={`app-dashboard-helper__toggle${open ? " app-dashboard-helper__toggle--open" : ""}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls="dashboard-next-steps-panel"
      >
        <HelpOutlineIcon className="app-dashboard-helper__icon" fontSize="small" />
        <span className="app-dashboard-helper__label">Что дальше?</span>
        <span className="app-dashboard-helper__count">
          {doneCount}/{total}
        </span>
        <ExpandLessIcon
          className={`app-dashboard-helper__chevron${open ? "" : " app-dashboard-helper__chevron--closed"}`}
          fontSize="small"
        />
      </button>

      {open ? (
        <div
          id="dashboard-next-steps-panel"
          className="app-dashboard-helper__panel"
          role="region"
          aria-label="Шаги запуска обучения"
        >
          <p className="app-dashboard-helper__intro">
            Помощник по запуску программы для команды
          </p>
          <OnboardingChecklist
            businessSubscription={businessSubscription}
            games={games}
            showHeader={false}
            className="app-onboarding--helper"
          />        </div>
      ) : null}
    </div>
  );
}

export default function DashboardTopBar({ businessSubscription, storage, games, teamUsage }) {
  return (
    <div className="app-dashboard-toolbar">
      <DashboardSubscriptionChip
        businessSubscription={businessSubscription}
        storage={storage}
        teamUsage={teamUsage}
      />
      <DashboardNextStepsHelper
        businessSubscription={businessSubscription}
        games={games}
      />    </div>
  );
}
